/**
 * Coalesces "the unread numbers may have changed" into one re-fetch.
 *
 * The badges (header chat icon, bottom tab, Agapella launcher) read their
 * counts from `GET /api/chat/channels/unread-summary`. Until now the only
 * things that triggered that read were a mount, a 30 s poll, web focus, and the
 * local read signal — so a message arriving while the user sat on the page
 * showed up whenever the poll got round to it, which reads as «появляется
 * только после обновления страницы». The app has had a live per-user
 * `MessageNotify` fan-out since CORE-621 (`chatPresenceHub`); this is what lets
 * the badges ride it without one GET per message.
 *
 * Trailing, not leading, and deliberately slower than
 * {@link ./readPointer.READ_POINTER_DEBOUNCE_MS}: a message landing in the
 * thread the user is *reading* is marked read ~600 ms later, and a leading
 * refresh would flash a badge for one second on a conversation that is open on
 * screen. Waiting out the read pointer means the number we fetch is the one the
 * user should actually see. A second is still immediate next to 30 s.
 *
 * Kept as a plain object, like `createReadPointer`, so it is testable without a
 * React renderer.
 */
export interface UnreadRefresher {
  /** Note that something may have changed; re-fetches once the burst settles. */
  bump(): void;
  /** Drop any pending re-fetch (hook unmounted / signed out). */
  cancel(): void;
}

export interface UnreadRefresherOptions {
  /** Re-read the authoritative counts. Called at most once per settled burst. */
  refresh: () => void;
  /** Quiet period before re-reading. */
  delayMs?: number;
}

/** Long enough for an open thread's read pointer (600 ms) to land first. */
export const UNREAD_REFRESH_DEBOUNCE_MS = 1000;

export function createUnreadRefresher({
  refresh,
  delayMs = UNREAD_REFRESH_DEBOUNCE_MS,
}: UnreadRefresherOptions): UnreadRefresher {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  return {
    bump() {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (!cancelled) refresh();
      }, delayMs);
    },
    cancel() {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
